import { TaskListUnit } from '../gql/serverTypes';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../../graphql/conflictResolution/types';

/**
 * Conflict handler for TaskListUnit entities.
 * Handles version conflicts during task list unit updates with intelligent merging.
 */
export const taskListUnitConflictHandler: ConflictHandler<TaskListUnit> = {
    entityType: 'TaskListUnit',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<TaskListUnit> {
        // Note: This is a fallback. The server should include currentState in conflict response.
        // If we need to refetch, we'd need workspaceId which we don't have here.
        // For now, throw an error - the server should always provide currentState.
        throw new Error(
            'TaskListUnit refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<TaskListUnit>): Partial<TaskListUnit> {
        const merged: Partial<TaskListUnit> = {
            // Always use current version
            version: currentState.version,
            // Preserve ID
            id: currentState.id
        };

        // Apply intended changes
        if (intendedChanges.name !== undefined) {
            merged.name = intendedChanges.name;
        }
        if (intendedChanges.description !== undefined) {
            merged.description = intendedChanges.description;
        }
        if (intendedChanges.avatarPath !== undefined) {
            merged.avatarPath = intendedChanges.avatarPath;
        }
        if (intendedChanges.timezone !== undefined) {
            merged.timezone = intendedChanges.timezone;
        }
        if (intendedChanges.logicalDayShift !== undefined) {
            merged.logicalDayShift = intendedChanges.logicalDayShift;
        }
        if (intendedChanges.isArchived !== undefined) {
            merged.isArchived = intendedChanges.isArchived;
        }
        if (intendedChanges.taskLists !== undefined) {
            merged.taskLists = intendedChanges.taskLists;
        }

        // Special handling for taskListsOrder - intelligent merge
        if (intendedChanges.taskListsOrder !== undefined && currentState.taskListsOrder !== undefined) {
            merged.taskListsOrder = mergeTaskListOrder(
                currentState.taskListsOrder,
                intendedChanges.taskListsOrder
            );
        } else if (intendedChanges.taskListsOrder !== undefined) {
            merged.taskListsOrder = intendedChanges.taskListsOrder;
        }

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};

/**
 * Intelligently merge task list order changes.
 * Preserves user's reordering intent while respecting server-side additions/deletions.
 * 
 * @param currentOrder - Current order from server
 * @param intendedOrder - Order the user intended to set
 * @returns Merged order that respects both changes
 */
function mergeTaskListOrder(
    currentOrder: string[],
    intendedOrder: string[]
): string[] {
    // Find items that were added on server (not in intended order)
    const addedItems = currentOrder.filter(id => !intendedOrder.includes(id));
    
    // Find items that are still valid (exist in current order)
    const validItems = intendedOrder.filter(id => currentOrder.includes(id));
    
    // Append new items at the end
    return [...validItems, ...addedItems];
}
